import React, { useState } from 'react';
import Typography from '@mui/material/Typography';
import { useQuery } from '@apollo/client';
import { Alert, Collapse, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { SearchIcon } from 'lucide-react';
import SalesPagesTable, { IRowData } from '../../components/SalesPagesTable';
import GET_SALES_PAGES from './queries/getSalesPages';
import { getErrorCode } from '../../utils/apollo';
import { noAccessErrorMessage } from '../../Routing';
import { Input } from '../../components/ui/input';
import { fuzzySearch } from '../../utils/fuzzySearch';

const SalesPages: React.FC = () => {
    const { t } = useTranslation();

    const [errorMessage, setErrorMessage] = useState<string>();
    const [searchTerm, setSearchTerm] = useState('');

    const { data: salesPagesData } = useQuery(GET_SALES_PAGES, {
        variables: {
            searchTerm: '%%',
        },
        onError: (error) => {
            if (getErrorCode(error) === 'jwt-missing-role-claims') {
                setErrorMessage(noAccessErrorMessage);
            } else {
                setErrorMessage(t('error.fetch-sales-pages'));
            }
        },
    });

    const salesPages = fuzzySearch<IRowData>(
        (salesPagesData?.sales_pages ?? []).map((s: IRowData) => ({
            ...s,
            fullName: `${s.salesPagesUsers.firstName} ${s.salesPagesUsers.lastName}`,
        })),
        searchTerm,
        ['salesPagesUsers.email', 'fullName', 'publicPath', 'serviceName'],
    );

    if (errorMessage === noAccessErrorMessage) {
        return (
            <Alert severity="error" sx={{ mb: 2 }}>
                {t(noAccessErrorMessage)}
            </Alert>
        );
    }

    return (
        <div className="container">
            <Collapse in={!!errorMessage}>
                <Alert
                    severity="error"
                    action={
                        <IconButton
                            aria-label="close"
                            color="inherit"
                            size="small"
                            onClick={() => {
                                setErrorMessage(undefined);
                            }}
                        >
                            <CloseIcon fontSize="inherit" />
                        </IconButton>
                    }
                    sx={{ mb: 2 }}
                >
                    {errorMessage}
                </Alert>
            </Collapse>
            <Typography
                variant="h5"
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    fontWeight: 700,
                    margin: 3,
                }}
            >
                {t('menu.sales-pages')}
            </Typography>

            <div className="flex flex-col gap-2 my-6">
                <div className="relative">
                    <SearchIcon className="absolute top-1/2 left-2 -translate-y-1/2 size-4" />
                    <Input
                        id="salesPageSearch"
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={t('sales-page.search-sales-pages')}
                        className="max-w-80 pl-8"
                    />
                </div>
            </div>

            <SalesPagesTable salesPages={salesPages} />
        </div>
    );
};

export default SalesPages;
